import React, { useEffect } from 'react';

const MVCCConcurrencyControl = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'MVCC, OCC, PCC - Database Concurrency Control Guide 2024';
    
    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master database concurrency control - MVCC, Optimistic, and Pessimistic concurrency control. Learn transaction isolation and conflict resolution.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master database concurrency control - MVCC, Optimistic, and Pessimistic concurrency control. Learn transaction isolation and conflict resolution.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    // Add keywords meta tag
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'MVCC, multi-version concurrency control, optimistic concurrency control, pessimistic concurrency control, database transactions, isolation levels');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'MVCC, multi-version concurrency control, optimistic concurrency control, pessimistic concurrency control, database transactions, isolation levels';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      // Cleanup - restore original title
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    code: {
      background: '#f5f5f5',
      padding: '0.2rem 0.4rem',
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#333'
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    },
    blockquote: {
      borderLeft: '4px solid #008080',
      paddingLeft: '1rem',
      margin: '1rem 0',
      fontStyle: 'italic',
      color: '#555'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Database Concurrency Control</h1>
        <p style={styles.subtitle}>
          MVCC, Optimistic, and Pessimistic Concurrency Control Explained
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>What is Concurrency Control?</h2>
          <p style={styles.paragraph}>
            Concurrency control is a database management technique that ensures transactions execute 
            in a safe manner when multiple transactions are running simultaneously. It prevents data 
            inconsistencies and maintains database integrity while maximizing performance.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>1. Multi-Version Concurrency Control (MVCC)</h2>
          <p style={styles.paragraph}>
            MVCC allows multiple versions of the same data to exist simultaneously. Each transaction 
            sees a consistent snapshot of the database at a specific point in time, eliminating the 
            need for read locks and improving concurrency.
          </p>

          <h3 style={styles.heading}>How MVCC Works</h3>
          <div style={styles.codeBlock}>
{`// MVCC Example - PostgreSQL-style implementation

// Each row has multiple versions with timestamps
class MVCCRow {
    constructor(data, xmin, xmax = null) {
        this.data = data;           // Actual row data
        this.xmin = xmin;           // Transaction ID that created this version
        this.xmax = xmax;           // Transaction ID that deleted this version (null if active)
        this.timestamp = Date.now(); // Creation timestamp
    }
    
    // Check if this version is visible to a transaction
    isVisibleTo(transactionId, transactionStartTime) {
        // Version is visible if:
        // 1. It was created before our transaction started
        // 2. It hasn't been deleted, or deletion happened after our transaction started
        return this.xmin < transactionId && 
               (this.xmax === null || this.xmax > transactionId);
    }
}

class MVCCTable {
    constructor(name) {
        this.name = name;
        this.versions = new Map(); // rowId -> [versions]
        this.nextRowId = 1;
    }
    
    // Insert new row
    insert(data, transactionId) {
        const rowId = this.nextRowId++;
        const version = new MVCCRow(data, transactionId);
        
        if (!this.versions.has(rowId)) {
            this.versions.set(rowId, []);
        }
        this.versions.get(rowId).push(version);
        
        return rowId;
    }
    
    // Update row (creates new version)
    update(rowId, newData, transactionId) {
        const versions = this.versions.get(rowId);
        if (!versions) {
            throw new Error(\`Row \${rowId} not found\`);
        }
        
        // Find the current active version
        const currentVersion = versions.find(v => v.xmax === null);
        if (currentVersion) {
            // Mark current version as deleted by this transaction
            currentVersion.xmax = transactionId;
        }
        
        // Create new version
        const newVersion = new MVCCRow(newData, transactionId);
        versions.push(newVersion);
        
    }
    
    // Delete row (marks current version as deleted)
    delete(rowId, transactionId) {
        const versions = this.versions.get(rowId);
        if (!versions) {
            throw new Error(\`Row \${rowId} not found\`);
        }
        
        const currentVersion = versions.find(v => v.xmax === null);
        if (currentVersion) {
            currentVersion.xmax = transactionId;
        }
    }
    
    // Read row as it appears to a specific transaction
    select(rowId, transactionId, transactionStartTime) {
        const versions = this.versions.get(rowId);
        if (!versions) {
            return null;
        }
        
        // Find the version visible to this transaction
        for (let i = versions.length - 1; i >= 0; i--) {
            const version = versions[i];
            if (version.isVisibleTo(transactionId, transactionStartTime)) {
                return version.data;
            }
        }
        
        return null; // No visible version
    }
    
    // Get all visible rows for a transaction
    selectAll(transactionId, transactionStartTime) {
        const result = [];
        
        for (const [rowId, versions] of this.versions) {
            const data = this.select(rowId, transactionId, transactionStartTime);
            if (data) {
                result.push({ rowId, data });
            }
        }
        
        return result;
    }
    
    // Cleanup old versions (vacuum process)
    vacuum(oldestActiveTransaction) {
        for (const [rowId, versions] of this.versions) {
            // Remove versions that are no longer visible to any active transaction
            const cleanVersions = versions.filter(version => {
                return version.xmax === null || version.xmax >= oldestActiveTransaction;
            });
            
            if (cleanVersions.length < versions.length) {
                this.versions.set(rowId, cleanVersions);
            }
        }
    }
}

// MVCC Transaction Manager
class MVCCTransactionManager {
    constructor() {
        this.nextTransactionId = 1;
        this.activeTransactions = new Map();
        this.tables = new Map();
    }
    
    beginTransaction() {
        const transactionId = this.nextTransactionId++;
        const transaction = {
            id: transactionId,
            startTime: Date.now(),
            status: 'ACTIVE'
        };
        
        this.activeTransactions.set(transactionId, transaction);
        return transactionId;
    }
    
    commit(transactionId) {
        const transaction = this.activeTransactions.get(transactionId);
        if (transaction) {
            transaction.status = 'COMMITTED';
            this.activeTransactions.delete(transactionId);
        }
    }
    
    rollback(transactionId) {
        const transaction = this.activeTransactions.get(transactionId);
        if (transaction) {
            transaction.status = 'ABORTED';
            this.activeTransactions.delete(transactionId);
            // In real implementation, would undo changes
        }
    }
    
    getTable(tableName) {
        if (!this.tables.has(tableName)) {
            this.tables.set(tableName, new MVCCTable(tableName));
        }
        return this.tables.get(tableName);
    }
}

// Example Usage
const txManager = new MVCCTransactionManager();
const usersTable = txManager.getTable('users');

// Transaction 1
const tx1 = txManager.beginTransaction();
usersTable.insert({ name: 'Alice', age: 30 }, tx1);
usersTable.insert({ name: 'Bob', age: 25 }, tx1);

// Transaction 2 (concurrent)
const tx2 = txManager.beginTransaction();

txManager.commit(tx1);

// Now TX2 can see TX1's changes

// TX2 updates Alice
usersTable.update(1, { name: 'Alice', age: 31 }, tx2);

// Transaction 3 starts
const tx3 = txManager.beginTransaction();

txManager.commit(tx2);
`}
          </div>

          <h3 style={styles.heading}>MVCC Benefits</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>No Read Locks:</strong> Readers never block writers and vice versa</li>
            <li style={styles.listItem}><strong>Snapshot Isolation:</strong> Each transaction sees a consistent view</li>
            <li style={styles.listItem}><strong>High Concurrency:</strong> Multiple transactions can run simultaneously</li>
            <li style={styles.listItem}><strong>Deadlock Reduction:</strong> Fewer locking conflicts</li>
          </ul>

          <h3 style={styles.heading}>MVCC Drawbacks</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Storage Overhead:</strong> Multiple versions consume more space</li>
            <li style={styles.listItem}><strong>Vacuum Overhead:</strong> Need to clean up old versions</li>
            <li style={styles.listItem}><strong>Write Amplification:</strong> Updates create new versions</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>2. Optimistic Concurrency Control (OCC)</h2>
          <p style={styles.paragraph}>
            OCC assumes that conflicts between transactions are rare. Transactions execute without 
            locks, and conflicts are detected at commit time. If a conflict is detected, the 
            transaction is rolled back and retried.
          </p>

          <div style={styles.codeBlock}>
{`// Optimistic Concurrency Control Example

class OCCTransaction {
    constructor(id) {
        this.id = id;
        this.readSet = new Set();    // Objects read by this transaction
        this.writeSet = new Map();   // Objects written by this transaction
        this.startTime = Date.now();
        this.status = 'ACTIVE';
    }
    
    read(objectId, dataStore) {
        this.readSet.add(objectId);
        return dataStore.read(objectId);
    }
    
    write(objectId, value) {
        this.writeSet.set(objectId, value);
    }
    
    validate(dataStore) {
        // Check if any object in read set has been modified since transaction start
        for (const objectId of this.readSet) {
            const currentVersion = dataStore.getVersion(objectId);
            if (currentVersion > this.startTime) {
                return false; // Conflict detected
            }
        }
        
        // Check if any object in write set has been modified by another transaction
        for (const objectId of this.writeSet.keys()) {
            const lastModified = dataStore.getLastModified(objectId);
            if (lastModified > this.startTime) {
                return false; // Conflict detected
            }
        }
        
        return true; // No conflicts
    }
    
    commit(dataStore) {
        // Validation phase
        if (!this.validate(dataStore)) {
            this.status = 'ABORTED';
            throw new Error(\`Transaction \${this.id} aborted due to conflicts\`);
        }
        
        // Write phase - apply all writes atomically
        const commitTime = Date.now();
        for (const [objectId, value] of this.writeSet) {
            dataStore.write(objectId, value, commitTime);
        }
        
        this.status = 'COMMITTED';
    }
}

class OCCDataStore {
    constructor() {
        this.data = new Map();
        this.versions = new Map();     // objectId -> version timestamp
        this.lastModified = new Map(); // objectId -> last modification time
    }
    
    read(objectId) {
        return this.data.get(objectId);
    }
    
    write(objectId, value, timestamp) {
        this.data.set(objectId, value);
        this.versions.set(objectId, timestamp);
        this.lastModified.set(objectId, timestamp);
    }
    
    getVersion(objectId) {
        return this.versions.get(objectId) || 0;
    }
    
    getLastModified(objectId) {
        return this.lastModified.get(objectId) || 0;
    }
}

// Banking Example with OCC
class OCCBankingSystem {
    constructor() {
        this.dataStore = new OCCDataStore();
        this.transactionId = 1;
        
        // Initialize accounts
        this.dataStore.write('account_1', { balance: 1000 }, Date.now());
        this.dataStore.write('account_2', { balance: 500 }, Date.now());
    }
    
    transfer(fromAccount, toAccount, amount) {
        const tx = new OCCTransaction(this.transactionId++);
        
        try {
            // Read phase
            const fromBalance = tx.read(fromAccount, this.dataStore);
            const toBalance = tx.read(toAccount, this.dataStore);
            
            if (!fromBalance || fromBalance.balance < amount) {
                throw new Error('Insufficient funds');
            }
            
            // Write phase (local)
            tx.write(fromAccount, { balance: fromBalance.balance - amount });
            tx.write(toAccount, { balance: toBalance.balance + amount });
            
            // Simulate some processing time
            setTimeout(() => {
                try {
                    tx.commit(this.dataStore);
                } catch (error) {
                    // In practice, would retry the transaction
                }
            }, Math.random() * 100);
            
        } catch (error) {
        }
    }
    
    getBalance(accountId) {
        const account = this.dataStore.read(accountId);
        return account ? account.balance : 0;
    }
}

// Example usage
const bank = new OCCBankingSystem();

// Concurrent transfers that might conflict
bank.transfer('account_1', 'account_2', 100);
bank.transfer('account_2', 'account_1', 50);
bank.transfer('account_1', 'account_2', 200);

// Check final balances after all transactions
setTimeout(() => {
}, 500);`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>3. Pessimistic Concurrency Control (PCC)</h2>
          <p style={styles.paragraph}>
            PCC assumes that conflicts are likely and uses locks to prevent them. Transactions 
            acquire locks before accessing data and hold them until commit, ensuring serializable 
            execution but potentially reducing concurrency.
          </p>

          <div style={styles.codeBlock}>
{`// Pessimistic Concurrency Control Example

class Lock {
    constructor(type, transactionId) {
        this.type = type;           // 'SHARED' or 'EXCLUSIVE'
        this.transactionId = transactionId;
        this.timestamp = Date.now();
    }
}

class LockManager {
    constructor() {
        this.locks = new Map();     // objectId -> [locks]
        this.waitQueue = new Map(); // objectId -> [waiting transactions]
    }
    
    acquireLock(objectId, lockType, transactionId) {
        const existingLocks = this.locks.get(objectId) || [];
        
        // Check if lock can be granted
        if (this.canGrantLock(existingLocks, lockType, transactionId)) {
            const lock = new Lock(lockType, transactionId);
            existingLocks.push(lock);
            this.locks.set(objectId, existingLocks);
            return true;
        } else {
            // Add to wait queue
            const waitQueue = this.waitQueue.get(objectId) || [];
            waitQueue.push({ transactionId, lockType });
            this.waitQueue.set(objectId, waitQueue);
            return false;
        }
    }
    
    canGrantLock(existingLocks, requestedType, transactionId) {
        // Transaction already has a lock on this object
        if (existingLocks.some(lock => lock.transactionId === transactionId)) {
            return true;
        }
        
        // No existing locks
        if (existingLocks.length === 0) {
            return true;
        }
        
        // Shared locks are compatible with other shared locks
        if (requestedType === 'SHARED') {
            return existingLocks.every(lock => lock.type === 'SHARED');
        }
        
        // Exclusive locks are not compatible with any other locks
        return false;
    }
    
    releaseLock(objectId, transactionId) {
        const existingLocks = this.locks.get(objectId) || [];
        const updatedLocks = existingLocks.filter(lock => lock.transactionId !== transactionId);
        
        if (updatedLocks.length === 0) {
            this.locks.delete(objectId);
        } else {
            this.locks.set(objectId, updatedLocks);
        }
        
        
        // Process wait queue
        this.processWaitQueue(objectId);
    }
    
    processWaitQueue(objectId) {
        const waitQueue = this.waitQueue.get(objectId) || [];
        if (waitQueue.length === 0) return;
        
        const existingLocks = this.locks.get(objectId) || [];
        const processedQueue = [];
        
        for (const request of waitQueue) {
            if (this.canGrantLock(existingLocks, request.lockType, request.transactionId)) {
                const lock = new Lock(request.lockType, request.transactionId);
                existingLocks.push(lock);
            } else {
                processedQueue.push(request);
            }
        }
        
        this.locks.set(objectId, existingLocks);
        this.waitQueue.set(objectId, processedQueue);
    }
    
    releaseAllLocks(transactionId) {
        // Release all locks held by the transaction
        for (const [objectId, locks] of this.locks) {
            const hasLock = locks.some(lock => lock.transactionId === transactionId);
            if (hasLock) {
                this.releaseLock(objectId, transactionId);
            }
        }
    }
}

class PCCTransaction {
    constructor(id, lockManager) {
        this.id = id;
        this.lockManager = lockManager;
        this.locksHeld = new Set();
        this.status = 'ACTIVE';
    }
    
    read(objectId, dataStore) {
        // Acquire shared lock
        if (this.lockManager.acquireLock(objectId, 'SHARED', this.id)) {
            this.locksHeld.add(objectId);
            return dataStore.read(objectId);
        } else {
            throw new Error(\`Cannot acquire shared lock on \${objectId}\`);
        }
    }
    
    write(objectId, value, dataStore) {
        // Acquire exclusive lock
        if (this.lockManager.acquireLock(objectId, 'EXCLUSIVE', this.id)) {
            this.locksHeld.add(objectId);
            dataStore.write(objectId, value);
        } else {
            throw new Error(\`Cannot acquire exclusive lock on \${objectId}\`);
        }
    }
    
    commit() {
        // Release all locks
        for (const objectId of this.locksHeld) {
            this.lockManager.releaseLock(objectId, this.id);
        }
        this.locksHeld.clear();
        this.status = 'COMMITTED';
    }
    
    rollback() {
        // Release all locks
        for (const objectId of this.locksHeld) {
            this.lockManager.releaseLock(objectId, this.id);
        }
        this.locksHeld.clear();
        this.status = 'ABORTED';
    }
}

// Simple data store
class PCCDataStore {
    constructor() {
        this.data = new Map();
    }
    
    read(objectId) {
        return this.data.get(objectId);
    }
    
    write(objectId, value) {
        this.data.set(objectId, value);
    }
}

// Banking example with PCC
class PCCBankingSystem {
    constructor() {
        this.lockManager = new LockManager();
        this.dataStore = new PCCDataStore();
        this.transactionId = 1;
        
        // Initialize accounts
        this.dataStore.write('account_1', { balance: 1000 });
        this.dataStore.write('account_2', { balance: 500 });
    }
    
    transfer(fromAccount, toAccount, amount) {
        const tx = new PCCTransaction(this.transactionId++, this.lockManager);
        
        try {
            // Read balances (acquires shared locks)
            const fromBalance = tx.read(fromAccount, this.dataStore);
            const toBalance = tx.read(toAccount, this.dataStore);
            
            if (!fromBalance || fromBalance.balance < amount) {
                tx.rollback();
                throw new Error('Insufficient funds');
            }
            
            // Update balances (acquires exclusive locks)
            tx.write(fromAccount, { balance: fromBalance.balance - amount }, this.dataStore);
            tx.write(toAccount, { balance: toBalance.balance + amount }, this.dataStore);
            
            tx.commit();
            
        } catch (error) {
            tx.rollback();
        }
    }
    
    getBalance(accountId) {
        const account = this.dataStore.read(accountId);
        return account ? account.balance : 0;
    }
}

// Example usage
const pccBank = new PCCBankingSystem();

try {
    pccBank.transfer('account_1', 'account_2', 100);
    pccBank.transfer('account_2', 'account_1', 50);
    
} catch (error) {
    console.error('Banking operation failed:', error);
}`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Comparison Summary</h2>
          
          <div style={styles.codeBlock}>
{`| Aspect                | MVCC                    | OCC                     | PCC                     |
|-----------------------|-------------------------|-------------------------|-------------------------|
| Conflict Detection    | Version-based           | At commit time          | Before access           |
| Locking Strategy      | No read locks           | No locks during exec    | Locks throughout        |
| Concurrency Level     | High                    | Medium-High             | Low-Medium              |
| Storage Overhead      | High (versions)         | Low                     | Low                     |
| Deadlock Risk         | Low                     | None                    | High                    |
| Abort Rate            | Low                     | Medium (conflicts)      | Low                     |
| Best Use Case         | Read-heavy workloads    | Low conflict scenarios  | High conflict scenarios |
| Examples              | PostgreSQL, Oracle      | Git, CouchDB            | Traditional RDBMS       |`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>When to Use Each Approach</h2>
          
          <h3 style={styles.heading}>Use MVCC When:</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}>Read-heavy workloads dominate</li>
            <li style={styles.listItem}>Long-running analytical queries</li>
            <li style={styles.listItem}>High concurrency requirements</li>
            <li style={styles.listItem}>Storage space is not a primary concern</li>
          </ul>

          <h3 style={styles.heading}>Use OCC When:</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}>Conflicts are rare</li>
            <li style={styles.listItem}>Short transactions</li>
            <li style={styles.listItem}>Distributed systems</li>
            <li style={styles.listItem}>Want to avoid deadlocks completely</li>
          </ul>

          <h3 style={styles.heading}>Use PCC When:</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}>Conflicts are frequent</li>
            <li style={styles.listItem}>Strong consistency requirements</li>
            <li style={styles.listItem}>Traditional OLTP workloads</li>
            <li style={styles.listItem}>Predictable access patterns</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Conclusion</h2>
          <p style={styles.paragraph}>
            Each concurrency control mechanism has its strengths and trade-offs. MVCC provides 
            excellent read performance and concurrency, OCC works well for low-conflict scenarios, 
            and PCC ensures strong consistency at the cost of reduced concurrency. The choice 
            depends on your specific workload characteristics and requirements.
          </p>
        </section>
      </main>
    </article>
  );
};

export default MVCCConcurrencyControl;
